/**
 * Unified data-testid values for QA / Playwright / automation.
 * Use the same id for the same type of control across the app (e.g. all client dropdowns = DROPDOWN_CLIENT).
 */

// Dropdowns - generic by field type (use these when there is a single dropdown of that type in the view)
export const DROPDOWN_CLIENT = 'dropdown-client';
export const DROPDOWN_OFFICE = 'dropdown-office';
export const DROPDOWN_SHIFT = 'dropdown-shift';
export const DROPDOWN_DEPARTMENT = 'dropdown-department';
export const DROPDOWN_POSITION = 'dropdown-position';
export const DROPDOWN_WEEKDAY = 'dropdown-weekday';
export const DROPDOWN_DATE_OPTION = 'dropdown-date-option';
export const DROPDOWN_COMPANY = 'dropdown-company';
export const DROPDOWN_WEEK = 'dropdown-week';
export const DROPDOWN_REPORT_TYPE = 'dropdown-report-type';
export const DROPDOWN_PAYROLL_CYCLE = 'dropdown-payroll-cycle';
export const DROPDOWN_COUNTRY = 'dropdown-country';
export const DROPDOWN_REGION = 'dropdown-region';
export const DROPDOWN_STATE = 'dropdown-state';
export const DROPDOWN_GENDER = 'dropdown-gender';
export const DROPDOWN_MARITAL_STATUS = 'dropdown-marital-status';
export const DROPDOWN_MULTIPLE_JOBS = 'dropdown-multiple-jobs';
export const DROPDOWN_BRANCH = 'dropdown-branch';
export const DROPDOWN_STATUS = 'dropdown-status';
export const DROPDOWN_ROLE = 'dropdown-role';
export const DROPDOWN_TYPE = 'dropdown-type';
export const DROPDOWN_YEAR = 'dropdown-year';
export const DROPDOWN_TIMECLOCK = 'dropdown-timeclock';
export const DROPDOWN_JOB_LOCATION = 'dropdown-job-location';
export const DROPDOWN_DRUG_TEST = 'dropdown-drug-test';
export const DROPDOWN_BACKGROUND_TEST = 'dropdown-background-test';
export const DROPDOWN_WORK_REGION = 'dropdown-work-region';
export const DROPDOWN_SALESMAN = 'dropdown-salesman';
export const DROPDOWN_FILE_TYPE = 'dropdown-file-type';
export const DROPDOWN_CODE = 'dropdown-code';
export const DROPDOWN_GLOBAL_PARAMETERS = 'dropdown-global-parameters';
export const DROPDOWN_CLIENT_WEEK_RANGE = 'dropdown-client-week-range';
export const DROPDOWN_TAX_API = 'dropdown-tax-api';
export const DROPDOWN_TAX_BOOLEAN = 'dropdown-tax-boolean';
export const DROPDOWN_FUNDING_COMPANY = 'dropdown-funding-company';
export const DROPDOWN_GENERATE_EMPLOYEE_ID = 'dropdown-generate-employee-id';

// For lists where the same dropdown type appears multiple times (e.g. position per row), use a helper
export const dropdownWithSuffix = (baseId, suffix) => `${baseId}-${suffix}`;

// Advanced search panel
export const BTN_ADVANCE_SEARCH_FIND = 'advance-search-find-btn';
export const BTN_ADVANCE_SEARCH_CANCEL = 'advance-search-cancel-btn';
export const BTN_ADVANCE_SEARCH_CLEAN = 'advance-search-clean-btn';

// Table row actions
export const BTN_WORKERCOMP_DELETE = 'workercomp-table-delete-btn';

// Worker comp page and its add form
export const BTN_WORKER_COMP_CANCEL = 'worker-comp-cancel-btn';
export const BTN_WORKER_COMP_SAVE = 'worker-comp-save-btn';
export const BTN_WORKER_COMP_ADD_CANCEL = 'worker-comp-add-cancel-btn';
export const BTN_WORKER_COMP_ADD_SAVE = 'worker-comp-add-save-btn';
export const BTN_WORKER_COMP_ADD_CLEAN = 'worker-comp-add-clean-btn';

// Paginator
export const BTN_PAGINATOR_PREV = 'paginator-prev';
export const BTN_PAGINATOR_NEXT = 'paginator-next';
export const paginatorPageTestId = (page) => `paginator-page-${page}`;

// Column size tool (developer panel on the tables)
export const BTN_COLUMN_SIZE_TOOL_TOGGLE = 'column-size-tool-toggle';
export const BTN_COLUMN_SIZE_TOOL_CLOSE = 'column-size-tool-close';
export const BTN_COLUMN_SIZE_TOOL_RESET = 'column-size-tool-reset';
export const BTN_COLUMN_SIZE_TOOL_COPY = 'column-size-tool-copy';
export const columnSizeToolInputTestId = (label, key) => `column-size-tool-${label}-${key}`;

// Home landing page
export const BTN_HOME_GET_STARTED_SIGN_IN = 'home-get-started-sign-in-btn';

// Error boundary fallback
export const BTN_ERROR_BOUNDARY_RETRY = 'error-boundary-retry-btn';
export const BTN_ERROR_BOUNDARY_HOME = 'error-boundary-home-btn';

// Table toolbar, filters and empty state
export const BTN_TOOLBAR_ADVANCED_SEARCH = 'table-toolbar-advanced-search-btn';
export const BTN_FILTER_CHIPS_CLEAR = 'filter-chips-clear';
export const BTN_TABLE_EMPTY_CLEAR = 'table-empty-clear-btn';
export const filterChipRemoveTestId = (field) => `filter-chip-remove-${field}`;
export const tableFilterTestId = (field) => `table-filter-${field}`;

// One id per privilege of the actions cell (CREATE, UPDATE, DELETE, VIEW, ...).
// The row id keeps the value unique when the table renders many rows.
export const tableActionTestId = (action, rowId) => {
  const base = `table-action-${String(action).toLowerCase()}`;
  return rowId == null ? base : `${base}-${rowId}`;
};

// Time off admin — the day detail modal
export const TIMEOFF_SELECT_ALL_CHECKBOX = 'timeoff-select-all-checkbox';
export const TIMEOFF_ROW_CHECKBOX = 'timeoff-row-checkbox';
export const timeOffRowCheckboxId = (timeOffId) => `${TIMEOFF_ROW_CHECKBOX}-${timeOffId}`;

// Adjustment
export const BTN_ADJUSTMENT_SELECT_EMPLOYEES = 'adjustment-select-employees-btn';
export const INPUT_ADJUSTMENT_PAY = 'adjustment-pay-input';
export const BTN_ADJUSTMENT_ROW_INFO = 'adjustment-row-info-btn';
export const BTN_ADJUSTMENT_ROW_DUPLICATE = 'adjustment-row-duplicate-btn';
export const BTN_ADJUSTMENT_ROW_DELETE = 'adjustment-row-delete-btn';
export const BTN_ADJUSTMENT_CANCEL = 'adjustment-cancel-btn';
export const BTN_ADJUSTMENT_NEXT = 'adjustment-next-btn';
export const BTN_ADJUSTMENT_BACK = 'adjustment-back-btn';
export const BTN_ADJUSTMENT_SAVE = 'adjustment-save-btn';
export const INPUT_ADJUSTMENT_SAME_DATA_PAY = 'adjustment-same-data-pay-input';
export const BTN_ADJUSTMENT_SAME_DATA_UPDATE = 'adjustment-same-data-update-btn';
export const BTN_ADJUSTMENT_INFO_CANCEL = 'adjustment-info-cancel-btn';
export const BTN_ADJUSTMENT_INFO_SAVE = 'adjustment-info-save-btn';
export const BTN_ADJUSTMENT_INFO_REMOVE = 'adjustment-info-remove-btn';

// Read Invoice
export const DROPDOWN_READ_INVOICE_DEPARTMENT = 'dropdown-read-invoice-department';
export const DROPDOWN_READ_INVOICE_POSITION = 'dropdown-read-invoice-position';
export const DROPDOWN_READ_INVOICE_LINE_TYPE = 'dropdown-read-invoice-line-type';
export const BTN_READ_INVOICE_CANCEL = 'read-invoice-cancel-btn';
export const BTN_READ_INVOICE_DOWNLOAD = 'read-invoice-download-btn';
export const BTN_READ_INVOICE_SEARCH = 'read-invoice-search-btn';
export const BTN_READ_INVOICE_HOW_IT_WORKS = 'read-invoice-how-it-works-btn';

// Update Invoice
export const BTN_UPDATE_INVOICE_SAVE = 'update-invoice-save-btn';
export const BTN_UPDATE_INVOICE_HOURS = 'update-invoice-hours-btn';
export const BTN_UPDATE_INVOICE_CONFIRM_CANCEL = 'update-invoice-confirm-cancel-btn';
export const BTN_UPDATE_INVOICE_CONFIRM_SAVE = 'update-invoice-confirm-save-btn';
export const INPUT_UPDATE_INVOICE_FUNDING_ID = 'update-invoice-funding-id-input';
export const INPUT_UPDATE_INVOICE_TAXES = 'update-invoice-taxes-input';
export const INPUT_UPDATE_INVOICE_CREDITS = 'update-invoice-credits-input';
export const INPUT_UPDATE_INVOICE_ADDITIONAL_FEE = 'update-invoice-additional-fee-input';

// Shared buttons
export const BTN_CANCEL = 'cancel-btn';
export const BTN_PHONE_OFFICE_BACK = 'phone-office-back-btn';
export const BTN_PHONE_OFFICE_NEXT = 'phone-office-next-btn';
export const BTN_SIGNATURE_CLEAR = 'signature-clear-btn';
export const BTN_SIGNATURE_GET = 'signature-get-btn';
export const BTN_TABLE_ACCEPT_ROWS = 'table-accept-rows-btn';
export const BTN_UPLOAD_FILE = 'upload-file-btn';
export const BTN_DOWNLOAD_FILE = 'download-file-btn';
